import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { toast } from 'sonner';
import { ArrowLeft } from 'lucide-react';

const ACTION_OPTIONS = [
  { value: 'all', label: 'Alle acties' },
  { value: 'create', label: 'create' },
  { value: 'update', label: 'update' },
  { value: 'enable', label: 'enable' },
  { value: 'disable', label: 'disable' },
  { value: 'resend_invite', label: 'resend_invite' },
  { value: 'heal', label: 'heal' },
];

export default function AuditLog() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState('all');
  const [actorEmail, setActorEmail] = useState('');
  const [targetEmail, setTargetEmail] = useState('');
  const [expanded, setExpanded] = useState({});

  const fetchPage = async (cursor = null, append = false) => {
    setLoading(true);
    try {
      const params = { limit: 50 };
      if (cursor) params.cursor = cursor;
      if (action && action !== 'all') params.action = action;
      if (actorEmail.trim()) params.actor_email = actorEmail.trim().toLowerCase();
      if (targetEmail.trim()) params.target_email = targetEmail.trim().toLowerCase();
      const resp = await api.get('/admin/audit-log', { params });
      setItems((prev) => append ? [...prev, ...resp.data.items] : resp.data.items);
      setNextCursor(resp.data.next_cursor || null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Kon audit log niet laden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPage(null, false);
  }, [action]); // eslint-disable-line

  const handleApplyFilters = (e) => {
    e.preventDefault();
    fetchPage(null, false);
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="audit-log-page">
      <header className="bg-white border-b sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin/users')} data-testid="back-to-users">
              <ArrowLeft className="h-4 w-4 mr-1" /> Gebruikers
            </Button>
            <div>
              <h1 className="text-xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>Audit log</h1>
              <p className="text-xs text-gray-500">Append-only log van super_admin-mutaties</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6 space-y-4">
        <Card>
          <CardHeader>
            <form onSubmit={handleApplyFilters} className="flex flex-wrap items-end gap-3">
              <div>
                <label className="text-xs text-gray-500">Actie</label>
                <Select value={action} onValueChange={setAction}>
                  <SelectTrigger className="w-44" data-testid="audit-action-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ACTION_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-gray-500">Actor e-mail</label>
                <Input value={actorEmail} onChange={(e) => setActorEmail(e.target.value)} placeholder="vincent@dexm.be" data-testid="audit-actor-filter" />
              </div>
              <div>
                <label className="text-xs text-gray-500">Target e-mail</label>
                <Input value={targetEmail} onChange={(e) => setTargetEmail(e.target.value)} placeholder="marie@dexm.be" data-testid="audit-target-filter" />
              </div>
              <Button type="submit" data-testid="audit-apply-filters">Filter toepassen</Button>
            </form>
          </CardHeader>
          <CardContent>
            {loading && items.length === 0 ? (
              <p className="text-sm text-gray-500">Laden…</p>
            ) : items.length === 0 ? (
              <p className="text-sm text-gray-500" data-testid="audit-empty-state">Geen audit-entries gevonden.</p>
            ) : (
              <Table data-testid="audit-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Tijd</TableHead>
                    <TableHead>Actie</TableHead>
                    <TableHead>Actor</TableHead>
                    <TableHead>Target</TableHead>
                    <TableHead>Diff</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((it) => (
                    <TableRow key={it.id} data-testid={`audit-row-${it.id}`}>
                      <TableCell className="text-xs">{(it.timestamp || '').slice(0, 19).replace('T', ' ')}</TableCell>
                      <TableCell className="text-xs"><code>{it.action}</code></TableCell>
                      <TableCell className="text-xs">{it.actor_email || it.actor_user_id || '—'}</TableCell>
                      <TableCell className="text-xs">{it.target_email || '—'}</TableCell>
                      <TableCell className="text-xs">
                        <button
                          type="button"
                          onClick={() => setExpanded((prev) => ({ ...prev, [it.id]: !prev[it.id] }))}
                          className="text-blue-600 hover:underline"
                          data-testid={`audit-toggle-${it.id}`}
                        >
                          {expanded[it.id] ? 'Verbergen' : 'Tonen'}
                        </button>
                        {expanded[it.id] && (
                          <pre className="mt-2 bg-gray-50 border rounded p-2 max-w-xl overflow-auto text-[10px] whitespace-pre-wrap">
{JSON.stringify({ before: it.before, after: it.after }, null, 2)}
                          </pre>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            {nextCursor && (
              <div className="mt-4 text-center">
                <Button variant="outline" onClick={() => fetchPage(nextCursor, true)} disabled={loading} data-testid="audit-load-more">
                  {loading ? 'Laden…' : 'Meer laden'}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
